import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Progress } from "@/shared/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Eye } from "lucide-react";
import {
  ALL_FIELDS,
  REQUIRED_FIELDS,
  type ImportError,
  type ParsedProperty,
} from "./fields";

interface MappingStepProps {
  parsedData: ParsedProperty[];
  columnMapping: Record<string, string>;
  setColumnMapping: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  errors: ImportError[];
  importing: boolean;
  progress: number;
  onBack: () => void;
  onValidate: () => void;
  onImport: () => void;
}

export function MappingStep({
  parsedData,
  columnMapping,
  setColumnMapping,
  errors,
  importing,
  progress,
  onBack,
  onValidate,
  onImport,
}: MappingStepProps) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Column Mapping</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ALL_FIELDS.map((field) => (
              <div key={field.key} className="space-y-1">
                <label className="text-sm font-medium">
                  {field.label}
                  {REQUIRED_FIELDS.includes(field) && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </label>
                <Select
                  value={columnMapping[field.key] || ""}
                  onValueChange={(value) =>
                    setColumnMapping((prev) => ({ ...prev, [field.key]: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {Object.keys(parsedData[0] || {}).map((col) => (
                      <SelectItem key={col} value={col}>
                        {col}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={onBack} variant="outline">
              Back
            </Button>
            <Button onClick={onValidate}>
              <Eye className="h-4 w-4 mr-2" />
              Preview & Validate
            </Button>
          </div>
        </CardContent>
      </Card>

      {errors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">
              Validation Errors ({errors.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-40 overflow-y-auto space-y-2">
              {errors.slice(0, 10).map((error, index) => (
                <div key={index} className="text-sm bg-destructive/10 p-2 rounded">
                  Row {error.row}: {error.message} ({error.field})
                </div>
              ))}
              {errors.length > 10 && (
                <p className="text-sm text-muted-foreground">
                  And {errors.length - 10} more errors...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {errors.length === 0 && parsedData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-green-600">Ready to Import</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              {parsedData.length} properties ready for import
            </p>
            <Button onClick={onImport} disabled={importing} className="w-full">
              {importing ? (
                <>Importing... ({progress.toFixed(0)}%)</>
              ) : (
                <>Import {parsedData.length} Properties</>
              )}
            </Button>
            {importing && <Progress value={progress} className="mt-2" />}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
